/**
 * QuoteForm — 대량 발송 견적 요청 4단계 폼
 *
 * 각 step은 별도의 봇 메시지로 추가되며, "다음/이전"은 새 메시지를 dispatch한다.
 * (사용자가 스크롤로 이전 답변을 볼 수 있도록 메시지를 덮어쓰지 않음)
 *
 *   1. 발송 채널 (멀티셀렉트)
 *   2. 예상 발송량 (단일 선택, premium/enterprise는 강조 안내)
 *   3. 발송 빈도 (단일 선택)
 *   4. 연락처 + 동의 → 제출
 *
 * 데이터: frontend/src/data/quote-request.json
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import quoteRequestData from '@/data/quote-request.json';
import {
  MAX_LENGTHS,
  describeIssue,
  normalizeInput,
  validateContact,
} from '@/lib/utils/sanitize';
import type {
  QuoteFormData,
  QuoteStep,
  QuoteVolumeTier,
  QuoteContact,
} from '@/types/qa';

interface ChannelOpt {
  id: string;
  label: string;
  icon: string;
  subtitle?: string;
}
interface VolumeOpt {
  id: string;
  label: string;
  tier: QuoteVolumeTier;
  highlight?: boolean;
}
interface FrequencyOpt {
  id: string;
  label: string;
}

const FORM = quoteRequestData.form as {
  channels: ChannelOpt[];
  volumes: VolumeOpt[];
  frequencies: FrequencyOpt[];
  tier_hints: Record<'premium' | 'enterprise', string>;
  consent: { label: string; summary: string; policy_url: string };
};

interface QuoteFormProps {
  step: QuoteStep;
  formData: Partial<QuoteFormData>;
  onUpdate: (step: QuoteStep, data: Partial<QuoteFormData>) => void;
  onSubmit: (data: QuoteFormData) => void;
  onCancel: () => void;
}

export function QuoteForm({
  step,
  formData,
  onUpdate,
  onSubmit,
  onCancel,
}: QuoteFormProps) {
  switch (step) {
    case 1:
      return <Step1Channels formData={formData} onNext={onUpdate} onCancel={onCancel} />;
    case 2:
      return <Step2Volume formData={formData} onNext={onUpdate} />;
    case 3:
      return <Step3Frequency formData={formData} onNext={onUpdate} />;
    case 4:
      return <Step4Contact formData={formData} onSubmit={onSubmit} onBack={onUpdate} />;
  }
}

// ─────────────────────────────────────────────────
// 공통 UI
// ─────────────────────────────────────────────────

function StepHeader({ step, title }: { step: number; title: string }) {
  return (
    <div className="ml-flex ml-items-center ml-gap-2 ml-mb-2">
      <span
        className={cn(
          'ml-inline-flex ml-items-center ml-justify-center',
          'ml-w-5 ml-h-5 ml-rounded-full',
          'ml-bg-brand-600 ml-text-white ml-text-cb-xs ml-font-semibold'
        )}
      >
        {step}
      </span>
      <span className="ml-text-cb-base ml-font-semibold ml-text-ink">{title}</span>
      <span className="ml-text-cb-xs ml-text-mute ml-ml-auto">{step}/4</span>
    </div>
  );
}

export function PrimaryButton({
  children,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'ml-flex-1 ml-px-3 ml-py-2 ml-rounded-cb-md',
        'ml-text-cb-base ml-font-semibold',
        'ml-transition-colors ml-duration-150',
        disabled
          ? 'ml-bg-mute ml-text-white ml-cursor-not-allowed'
          : 'ml-bg-brand-600 ml-text-white ml-cursor-pointer hover:ml-bg-brand-500'
      )}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'ml-px-3 ml-py-2 ml-rounded-cb-md',
        'ml-text-cb-base ml-text-mute ml-cursor-pointer',
        'ml-bg-white ml-border ml-border-line',
        'hover:ml-bg-surface hover:ml-text-ink ml-transition-colors'
      )}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────
// Step 1: 발송 채널 (멀티셀렉트)
// ─────────────────────────────────────────────────

interface Step1Props {
  formData: Partial<QuoteFormData>;
  onNext: (step: QuoteStep, data: Partial<QuoteFormData>) => void;
  onCancel: () => void;
}

function Step1Channels({ formData, onNext, onCancel }: Step1Props) {
  const [selected, setSelected] = useState<string[]>(formData.channels ?? []);

  function toggle(id: string) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  }

  return (
    <div className="ml-flex ml-flex-col ml-gap-2">
      <StepHeader step={1} title="발송 채널을 선택해 주세요" />
      <div className="ml-text-cb-sm ml-text-mute ml-mb-1">
        하나 이상 선택할 수 있습니다.
      </div>

      <div className="ml-grid ml-grid-cols-2 ml-gap-2">
        {FORM.channels.map((ch) => {
          const on = selected.includes(ch.id);
          return (
            <button
              key={ch.id}
              type="button"
              onClick={() => toggle(ch.id)}
              className={cn(
                'ml-flex ml-flex-col ml-gap-1',
                'ml-px-3 ml-py-2.5 ml-rounded-cb-md ml-border',
                'ml-text-left ml-cursor-pointer ml-transition-all ml-duration-150',
                on
                  ? 'ml-bg-brand-50 ml-border-brand-600 ml-text-brand-600 ml-font-semibold'
                  : 'ml-bg-white ml-border-line ml-text-ink hover:ml-border-brand-600'
              )}
            >
              <div className="ml-flex ml-items-center ml-gap-2 ml-w-full">
                <span className="ml-text-base ml-leading-none">{ch.icon}</span>
                <span className="ml-text-cb-sm ml-flex-1 ml-truncate">
                  {ch.label}
                </span>
                {on && <span className="ml-text-cb-xs">✓</span>}
              </div>
              {ch.subtitle && (
                <span className="ml-text-cb-xs ml-text-mute ml-pl-6 ml-truncate">
                  {ch.subtitle}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="ml-flex ml-gap-2 ml-mt-2">
        <SecondaryButton onClick={onCancel}>취소</SecondaryButton>
        <PrimaryButton
          disabled={selected.length === 0}
          onClick={() => onNext(2, { ...formData, channels: selected })}
        >
          다음
        </PrimaryButton>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────
// Step 2: 예상 발송량 (단일 선택)
// ─────────────────────────────────────────────────

interface Step2Props {
  formData: Partial<QuoteFormData>;
  onNext: (step: QuoteStep, data: Partial<QuoteFormData>) => void;
}

function Step2Volume({ formData, onNext }: Step2Props) {
  const [selected, setSelected] = useState<string>(formData.volume ?? '');
  const selectedOpt = FORM.volumes.find((v) => v.id === selected);
  const tierHint =
    selectedOpt && (selectedOpt.tier === 'premium' || selectedOpt.tier === 'enterprise')
      ? FORM.tier_hints[selectedOpt.tier]
      : null;

  return (
    <div className="ml-flex ml-flex-col ml-gap-2">
      <StepHeader step={2} title="예상 발송량을 알려주세요" />

      <div className="ml-flex ml-flex-col ml-gap-1.5">
        {FORM.volumes.map((v) => {
          const on = selected === v.id;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => setSelected(v.id)}
              className={cn(
                'ml-flex ml-items-center ml-gap-2',
                'ml-px-3 ml-py-2.5 ml-rounded-cb-md ml-border',
                'ml-text-cb-base ml-text-left ml-cursor-pointer ml-transition-all ml-duration-150',
                on
                  ? 'ml-bg-brand-50 ml-border-brand-600 ml-text-brand-600 ml-font-semibold'
                  : v.highlight
                    ? 'ml-bg-white ml-border-brand-200 ml-text-ink hover:ml-border-brand-600'
                    : 'ml-bg-white ml-border-line ml-text-ink hover:ml-border-brand-600'
              )}
            >
              <span className="ml-flex-1">{v.label}</span>
              {v.highlight && (
                <span className="ml-text-cb-xs ml-text-accent">⭐</span>
              )}
              {on && <span className="ml-text-cb-xs">✓</span>}
            </button>
          );
        })}
      </div>

      {tierHint && (
        <div
          className={cn(
            'ml-mt-1 ml-p-2.5 ml-rounded-cb-sm',
            'ml-bg-brand-50 ml-border ml-border-brand-200',
            'ml-text-cb-sm ml-text-brand-600'
          )}
        >
          {tierHint}
        </div>
      )}

      <div className="ml-flex ml-gap-2 ml-mt-2">
        <SecondaryButton onClick={() => onNext(1, formData)}>이전</SecondaryButton>
        <PrimaryButton
          disabled={!selected}
          onClick={() => onNext(3, { ...formData, volume: selected })}
        >
          다음
        </PrimaryButton>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────
// Step 3: 발송 빈도 (단일 선택)
// ─────────────────────────────────────────────────

interface Step3Props {
  formData: Partial<QuoteFormData>;
  onNext: (step: QuoteStep, data: Partial<QuoteFormData>) => void;
}

function Step3Frequency({ formData, onNext }: Step3Props) {
  const [selected, setSelected] = useState<string>(formData.frequency ?? '');

  return (
    <div className="ml-flex ml-flex-col ml-gap-2">
      <StepHeader step={3} title="발송 빈도는 어떻게 되시나요?" />

      <div className="ml-flex ml-flex-col ml-gap-1.5">
        {FORM.frequencies.map((f) => {
          const on = selected === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setSelected(f.id)}
              className={cn(
                'ml-flex ml-items-center ml-gap-2',
                'ml-px-3 ml-py-2.5 ml-rounded-cb-md ml-border',
                'ml-text-cb-base ml-text-left ml-cursor-pointer ml-transition-all ml-duration-150',
                on
                  ? 'ml-bg-brand-50 ml-border-brand-600 ml-text-brand-600 ml-font-semibold'
                  : 'ml-bg-white ml-border-line ml-text-ink hover:ml-border-brand-600'
              )}
            >
              <span className="ml-flex-1">{f.label}</span>
              {on && <span className="ml-text-cb-xs">✓</span>}
            </button>
          );
        })}
      </div>

      <div className="ml-flex ml-gap-2 ml-mt-2">
        <SecondaryButton onClick={() => onNext(2, formData)}>이전</SecondaryButton>
        <PrimaryButton
          disabled={!selected}
          onClick={() => onNext(4, { ...formData, frequency: selected })}
        >
          다음
        </PrimaryButton>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────
// Step 4: 연락처 + 동의 → 제출
// ─────────────────────────────────────────────────

interface Step4Props {
  formData: Partial<QuoteFormData>;
  onSubmit: (data: QuoteFormData) => void;
  onBack: (step: QuoteStep, data: Partial<QuoteFormData>) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9\-+\s()]{8,}$/;

function Step4Contact({ formData, onSubmit, onBack }: Step4Props) {
  const initial: QuoteContact = formData.contact ?? {
    company: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  };
  const [contact, setContact] = useState<QuoteContact>(initial);
  const [consent, setConsent] = useState<boolean>(formData.consent_agreed ?? false);
  const [showConsentDetail, setShowConsentDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [safetyError, setSafetyError] = useState<string | null>(null);

  const isEmailValid = EMAIL_RE.test(contact.email);
  const isPhoneValid = PHONE_RE.test(contact.phone);
  const canSubmit =
    contact.company.trim().length > 0 &&
    contact.name.trim().length > 0 &&
    isEmailValid &&
    isPhoneValid &&
    consent &&
    !submitting;

  function update<K extends keyof QuoteContact>(key: K, value: QuoteContact[K]) {
    const sanitized =
      typeof value === 'string' ? (normalizeInput(value) as QuoteContact[K]) : value;
    setContact((prev) => ({ ...prev, [key]: sanitized }));
    if (safetyError) setSafetyError(null);
  }

  function handleSubmit() {
    if (!canSubmit) return;
    const issues = validateContact(contact);
    if (issues.length > 0) {
      setSafetyError(issues.map(describeIssue).join('\n'));
      return;
    }
    setSubmitting(true);
    const channels = formData.channels ?? [];
    const volume = formData.volume ?? '';
    const frequency = formData.frequency ?? '';
    onSubmit({
      channels,
      volume,
      frequency,
      contact,
      consent_agreed: consent,
    });
  }

  return (
    <div className="ml-flex ml-flex-col ml-gap-2">
      <StepHeader step={4} title="연락처를 입력해 주세요" />

      <div className="ml-flex ml-flex-col ml-gap-2">
        <Field label="회사명" required>
          <input
            type="text"
            value={contact.company}
            onChange={(e) => update('company', e.target.value)}
            placeholder="(주)메일링크"
            maxLength={MAX_LENGTHS.company}
            className={inputClass}
          />
        </Field>
        <Field label="담당자명" required>
          <input
            type="text"
            value={contact.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="홍길동"
            maxLength={MAX_LENGTHS.name}
            className={inputClass}
          />
        </Field>
        <Field
          label="이메일"
          required
          error={contact.email && !isEmailValid ? '올바른 이메일 형식이 아닙니다' : undefined}
        >
          <input
            type="email"
            value={contact.email}
            onChange={(e) => update('email', e.target.value)}
            placeholder="contact@company.co.kr"
            maxLength={MAX_LENGTHS.email}
            className={inputClass}
          />
        </Field>
        <Field
          label="연락처"
          required
          error={contact.phone && !isPhoneValid ? '올바른 연락처를 입력해 주세요' : undefined}
        >
          <input
            type="tel"
            value={contact.phone}
            onChange={(e) => update('phone', e.target.value)}
            placeholder="010-1234-5678"
            maxLength={MAX_LENGTHS.phone}
            className={inputClass}
          />
        </Field>
        <Field label="추가 문의사항">
          <textarea
            value={contact.message ?? ''}
            onChange={(e) => update('message', e.target.value)}
            rows={2}
            placeholder="발송 일정, 특수 요구사항 등"
            maxLength={MAX_LENGTHS.message}
            className={cn(inputClass, 'ml-resize-none')}
          />
        </Field>
      </div>

      <div
        className={cn(
          'ml-mt-1 ml-p-2.5 ml-rounded-cb-sm',
          'ml-bg-surface ml-border ml-border-line'
        )}
      >
        <label className="ml-flex ml-items-start ml-gap-2 ml-cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="ml-mt-0.5 ml-flex-shrink-0"
          />
          <span className="ml-text-cb-sm ml-text-ink-secondary">
            {FORM.consent.label}
          </span>
        </label>
        <button
          type="button"
          onClick={() => setShowConsentDetail((v) => !v)}
          className={cn(
            'ml-mt-1 ml-ml-6 ml-text-cb-xs ml-text-brand-500 ml-cursor-pointer',
            'hover:ml-underline'
          )}
        >
          {showConsentDetail ? '간략히 보기' : '자세히 보기'}
        </button>
        {showConsentDetail && (
          <div className="ml-mt-1 ml-ml-6 ml-text-cb-xs ml-text-mute ml-leading-relaxed">
            {FORM.consent.summary}{' '}
            <a
              href={FORM.consent.policy_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-text-brand-500 hover:ml-underline"
            >
              개인정보처리방침 ↗
            </a>
          </div>
        )}
      </div>

      {safetyError && (
        <div
          role="alert"
          className={cn(
            'ml-mt-1 ml-p-2.5 ml-rounded-cb-sm',
            'ml-bg-surface ml-border ml-border-danger',
            'ml-text-cb-sm ml-text-danger ml-whitespace-pre-line'
          )}
        >
          {safetyError}
        </div>
      )}

      <div className="ml-flex ml-gap-2 ml-mt-2">
        <SecondaryButton
          onClick={() =>
            onBack(3, { ...formData, contact, consent_agreed: consent })
          }
        >
          이전
        </SecondaryButton>
        <PrimaryButton disabled={!canSubmit} onClick={handleSubmit}>
          {submitting ? '제출 중...' : '견적 요청 제출'}
        </PrimaryButton>
      </div>
    </div>
  );
}

export const inputClass = cn(
  'ml-w-full ml-px-2.5 ml-py-1.5 ml-rounded-cb-sm',
  'ml-bg-white ml-border ml-border-line',
  'ml-text-cb-sm ml-text-ink ml-font-sans',
  'focus:ml-outline-none focus:ml-border-brand-600'
);

export interface FieldProps {
  label: string;
  required?: boolean;
  // exactOptionalPropertyTypes 환경에서 호출부가 조건식으로 string|undefined를 직접 넘길 수 있도록.
  error?: string | undefined;
  children: React.ReactNode;
}

export function Field({ label, required, error, children }: FieldProps) {
  return (
    <label className="ml-flex ml-flex-col ml-gap-0.5">
      <span className="ml-text-cb-xs ml-text-mute ml-font-medium">
        {label}
        {required && <span className="ml-text-danger ml-ml-0.5">*</span>}
      </span>
      {children}
      {error && <span className="ml-text-cb-xs ml-text-danger">{error}</span>}
    </label>
  );
}
